import React, { useState, useEffect } from 'react';
import "./Form.css";
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useAppData } from '../../context/AppDataContext';
import PageLoader from '../../components/PageLoader';
import { useApi } from '../../context/ApiContext';
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from '../../components/ErrorBoundary';
import ThankYouScreen from '../../components/ThankYou';

export default function Form({ onSubmit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const participantID = queryParams.get("id");

  const { formAPIData, isLoading: formLoading, isFetching: formFetching } = useAppData();
  const { API, ready } = useApi();
  const [errorCode, setErrorCode] = useState(null);
  const [thankYou, setThankYou] = useState(null);

  const initialFormData = {
    companyID: '',
    companyName: '',
    department: '',
    eduLevel: '',
    email: '',
    firstName: '',
    gender: '',
    lastName: '',
    phone: '',
    position: '',
    subs: '',
    timeInPositionMonth: '',
    timeInPositionYear: '',
    yearJoined: '',
    yearOfBirth: '',
    status: '',
    updateStatus: '',
  };

  useEffect(() => {
    if (!participantID) setErrorCode("FORM_INVALID_ID");
  }, [participantID, navigate]);

  const participantQuery = useQuery({
    queryKey: ['participantData', participantID],
    queryFn: async () => {
      if (!participantID) return null;

      const res = await API.get(`/document/${participantID}?dataSource=rvcustdatascope`);
      if (res.data.Form !== "Participant") return null;
      return res.data || null;
    },
    enabled: !!participantID && ready, // only run if participantID exists and API is ready
    retry: 1,
  });

  const [formData, setFormData] = useState(initialFormData);

  const companyQuery = useQuery({
    queryKey: ['companyData', formData.companyID],
    queryFn: async () => {
      if (!formData.companyID) return null;

      const res = await API.get(`/document/${formData.companyID}?dataSource=rvcustdatascope`);
      return res.data || null;
    },
    enabled: !!formData.companyID && ready,
    retry: 1,
  })

  useEffect(() => {
    if (!ready) return;
    if (participantQuery.isLoading) return;
    const data = participantQuery.data;

    if (!data || Object.keys(data).length === 0) {
      setThankYou("Access to update removed or account not found.")
      return;
    }

    const monthYearMatch = data.TimeInPosition?.match(/(\d+)\s*month\(s\)\s*(\d+)\s*year\(s\)/) ?? [];
    const month = monthYearMatch[1] ?? '';
    const year = monthYearMatch[2] ?? '';

    setFormData({
      companyID: data.CompanyID || '',
      firstName: data.Firstname || '',
      lastName: data.LastName || '',
      email: data.Email || '',
      phone: data.Phone || '',
      gender: data.Gender || '',
      yearOfBirth: data.YearOfBirth ? String(data.YearOfBirth) : '',
      yearJoined: data.YearJoined ? String(data.YearJoined) : '',
      eduLevel: data.EduLevel || '',
      companyName: data.CompanyName || '',
      department: data.Department || '',
      position: data.Position || '',
      subs: data.Subs ? String(data.Subs) : '',
      timeInPositionMonth: month,
      timeInPositionYear: year,
      status: data.Status || '',
      updateStatus: data.updateStatus || '',
    });

  }, [participantQuery.isLoading, participantQuery.data, ready]);

  const handleChange = (e) => {
    const {name, value } = e.target;

    if (name === "phone") {
      const regex = /^[0-9+]*$/;
      if (!regex.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Combine months and years into one string
    const combinedTime = `${formData.timeInPositionMonth || 0} month(s) ${formData.timeInPositionYear || 0} year(s)`;

    const submissionData = {
      CompanyID: formData.companyID?.toString() || "",
      CompanyName: formData.companyName?.toString() || "",
      Department: formData.department?.toString() || "",
      EduLevel: formData.eduLevel?.toString() || "",
      Email: formData.email?.toString() || "",
      Firstname: formData.firstName?.toString() || "",
      Gender: formData.gender?.toString() || "",
      LastName: formData.lastName?.toString() || "",
      ParticipantID: participantID?.toString() || "",
      Phone: formData.phone?.toString() || "",
      Position: formData.position?.toString() || "",
      Status: "Active",
      Subs: formData.subs ? parseInt(formData.subs, 10).toString() : "0", 
      timeInPosition: combinedTime?.toString() || "",
      updateStatus: "Completed",
      YearJoined: formData.yearJoined ? parseInt(formData.yearJoined, 10).toString() : "",
      YearOfBirth: formData.yearOfBirth ? parseInt(formData.yearOfBirth, 10).toString() : "",
    };

    API.put(`/document/${participantID}?dataSource=rvcustdatascope`, submissionData).then(() => {
      setThankYou("Thank you for completing the form. You may now close this window.");
    }).catch(() => {
      setErrorCode("SERVER_ERROR");
    });
  };

  if (!ready || formLoading || formFetching || participantQuery.isLoading) {
    return <PageLoader />; // show loader until API is ready and data fetched
  }

  if (errorCode) {
    return <ErrorBoundary code={errorCode} />;
  }

  if (thankYou) {
    return <ThankYouScreen message={thankYou} />;
  }

  return (
    <div className="form-wrapper">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <label className="header-label">
            Thank you for participating. <br /><br /> Please confirm the information below:
          </label>

          <div className="form-row">
            <label>
              First Name: <span className="required">*</span>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Last Name: <span className="required">*</span>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Gender: <span className="required">*</span>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Gender --</option>
                {formAPIData["Gender"]?.map(d => (
                  <option key={d.id} value={d.description}>{d.description}</option>
                ))}
              </select>
            </label>

            <label>
              Birth Year:
              <span className="required-warning">
                <span className="required">*</span>
                {formData.yearOfBirth && new Date().getFullYear() - formData.yearOfBirth < 18 && (
                  <span className="warning">Participant is under 18 years old.</span>
                )}
                {formData.yearOfBirth && ((parseInt(formData.yearOfBirth, 10) < 1900 || parseInt(formData.yearOfBirth, 10) > new Date().getFullYear()) && (
                    <span className="warning">Enter a valid year.</span>
                  )
                )}
              </span> 
              <input
                type="text"
                name="yearOfBirth"
                value={formData.yearOfBirth}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setFormData((prev) => ({ ...prev, yearOfBirth: val }));
                  }
                }}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              E-mail: <span className="required">*</span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Phone: <span className="required">*</span>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Educational Level: <span className="required">*</span>
              <select
                name="eduLevel"
                value={formData.eduLevel}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Educational Level --</option>
                {formAPIData["Educational level"]?.map(d => (
                  <option key={d.id} value={d.description}>{d.description}</option>
                ))}
              </select>
            </label>

            <label>
              Company: <span className="required">*</span>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                readOnly
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Department: <span className="required">*</span>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Department --</option>
                {companyQuery.data?.Departments?.map((dep, idx) => (
                  <option key={idx} value={dep}>{dep}</option>
                ))}
              </select>
            </label>

            <label>
              Position: <span className="required">*</span>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Number of Subordinates: <span className="required">*</span>
              <input
                type="text"
                name="subs"
                value={formData.subs}
                onChange={(e) => {
                  let val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    if (val.length > 1) {
                      val = String(parseInt(val, 10)); // normalize number
                    }
                    setFormData((prev) => ({ ...prev, subs: val }));
                  }
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                required
              />
            </label>

            <label>
              Year Joined: 
              <span className="required-warning">
                  <span className="required">*</span>
                {formData.yearJoined && formData.yearOfBirth && formData.yearJoined < formData.yearOfBirth && (
                  <span className="warning">Year Joined is before Birth Year.</span>
                )}
                {formData.yearJoined && (
                  (parseInt(formData.yearJoined, 10) < 1900 ||
                  parseInt(formData.yearJoined, 10) > new Date().getFullYear()) && (
                    <span className="warning">Enter a valid year.</span>
                  )
                )}
              </span>
              <input
                type="text"
                name="yearJoined"
                value={formData.yearJoined}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setFormData((prev) => ({ ...prev, yearJoined: val }));
                  }
                }}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Time in Position (Months): <span className="required">*</span>
              <input
                type="text"
                name="timeInPositionMonth"
                value={formData.timeInPositionMonth}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    if (val === "" || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 11)) {
                      setFormData((prev) => ({ ...prev, timeInPositionMonth: val }));
                    }
                  }
                }}
                required
              />
            </label>

            <label>
              Time in Position (Years): <span className="required">*</span>
              <input
                type="text"
                name="timeInPositionYear"
                value={formData.timeInPositionYear}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    if (val === "" || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 100)) {
                      setFormData((prev) => ({ ...prev, timeInPositionYear: val }));
                    }
                  }
                }}
                required
              />
            </label>
          </div>

          <div className="btn-submit">
            <button type="submit">Submit</button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
}